import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { Loader2, Building2, ChevronLeft } from "lucide-react";

/**
 * iter260 — Admin: Business users archive (read-only).
 * Strictly separate from the individual archive.
 */
const NA = "غير متوفر تاريخيًا";
const fmt = (dt) => { if (!dt) return NA; try { return new Date(dt).toLocaleString("ar-SA", { hour12: false }); } catch { return NA; } };

export default function BusinessArchiveTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await apiClient.get("/admin/archive/businesses", { params: { q, city } });
      setRows(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [q, city]);

  useEffect(() => { load(); }, [load]);

  const open = async (bid) => {
    setSelected(bid); setDetail(null);
    try {
      const { data } = await apiClient.get(`/admin/archive/businesses/${bid}`);
      setDetail(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  if (selected && detail) {
    const { business: b, timeline, documents, listings, where } = detail;
    return (
      <div className="space-y-4" data-testid="business-detail">
        <button onClick={() => { setSelected(null); setDetail(null); }} className="btn-ghost text-xs">
          <ChevronLeft className="w-4 h-4" /> العودة
        </button>
        <div className="surface rounded-xl p-4">
          <h3 className="font-display text-xl mb-2">{b.name_ar || b.name_en}</h3>
          <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#3a3a3a]">
            <div>النشاط: {b.category || NA}</div>
            <div>البريد: {b.contact_email || NA}</div>
            <div>الجوال: {b.phone || NA}</div>
            <div>المدن: {(b.cities || []).join("، ") || NA}</div>
            <div>إنشاء الحساب: {fmt(b.created_at)}</div>
            <div>آخر نشاط: {fmt(b.updated_at)}</div>
          </div>
        </div>
        <div className="surface rounded-xl p-4">
          <h4 className="font-bold mb-2">أين وصل؟</h4>
          <div className="text-sm grid sm:grid-cols-2 gap-1">
            <div>آخر خطوة مكتملة: {where.last_completed || NA}</div>
            <div>الخطوة الحالية: {where.current_step || NA}</div>
            <div>حالة التسجيل: {where.registration_status}</div>
            <div>حالة التوثيق: {where.verification_status}</div>
            <div>آخر نشاط: {fmt(where.last_activity_at)}</div>
            <div>سبب التوقف: {where.stop_reason || "غير محدد"}</div>
          </div>
          {where.missing_documents?.length > 0 && (
            <div className="text-xs text-red-700 mt-2">مستندات ناقصة: {where.missing_documents.join("، ")}</div>
          )}
        </div>
        <div className="surface rounded-xl p-4">
          <h4 className="font-bold mb-2">السجل الزمني</h4>
          {timeline.length === 0 ? <p className="text-xs text-[#8a8a8a]">لا أحداث مسجلة.</p> : (
            <ol className="space-y-2 text-sm">
              {timeline.map((e, i) => (
                <li key={i} className="flex gap-3 border-b border-[#0a0a0a]/6 pb-2 last:border-0">
                  <span className="text-[#8a6f2e] font-mono text-xs shrink-0">{fmt(e.at)}</span>
                  <span>{e.label_ar}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        {documents.length > 0 && (
          <div className="surface rounded-xl p-4">
            <h4 className="font-bold mb-2">المستندات ({documents.length})</h4>
            <div className="space-y-2 text-xs">
              {documents.map((d) => (
                <div key={d.id} className="flex justify-between border-b border-[#0a0a0a]/6 pb-1">
                  <span>{d.kind || "مستند"}</span>
                  <span>{d.status || "—"}</span>
                  <span className="text-[#8a6f2e]">{fmt(d.uploaded_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="surface rounded-xl p-4">
          <h4 className="font-bold mb-2">سجل الإعلانات ({listings.length})</h4>
          {listings.length === 0 ? <p className="text-xs text-[#8a8a8a]">لا إعلانات.</p> : (
            <div className="space-y-2">
              {listings.map((v) => (
                <div key={v.id} className="border border-[#0a0a0a]/8 rounded-lg p-3 text-xs">
                  <div className="flex justify-between font-semibold text-sm">
                    <span>{v.title || v.id}</span>
                    <span className="text-[#8a6f2e]">{v.status || "—"}</span>
                  </div>
                  <div className="text-[#6b6b6b] mt-1 space-y-0.5">
                    <div>نشر: {fmt(v.created_at)}</div>
                    {v.updated_at && v.updated_at !== v.created_at && <div>تعديل: {fmt(v.updated_at)}</div>}
                    {v.deleted_at && <div>حذف: {fmt(v.deleted_at)}</div>}
                    {v.sold_at && <div>بيع: {fmt(v.sold_at)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="business-archive">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#8a6f2e]" /> أرشيف المستخدمين التجاريين
        </h2>
        <button onClick={load} disabled={loading} className="btn-ghost text-xs">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />} تحديث
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        <input placeholder="بحث بالاسم/البريد/الجوال" value={q} onChange={(e) => setQ(e.target.value)} className="input-dark max-w-xs" />
        <input placeholder="المدينة" value={city} onChange={(e) => setCity(e.target.value)} className="input-dark max-w-xs" />
      </div>
      {error && <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-800">{error}</div>}
      {loading && rows.length === 0 ? (
        <div className="text-[#6b6b6b] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />جاري التحميل…</div>
      ) : rows.length === 0 ? (
        <div className="surface rounded-xl p-8 text-center text-[#6b6b6b]">لا حسابات تجارية.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((b) => (
            <div key={b.id} className="surface rounded-xl p-3 flex justify-between items-center hover:border-[#c9a55c] cursor-pointer" onClick={() => open(b.id)} data-testid={`biz-row-${b.id}`}>
              <div>
                <div className="font-semibold">{b.name_ar || b.name_en}</div>
                <div className="text-xs text-[#6b6b6b]">{b.contact_email || "—"} · {(b.cities || [])[0] || "—"} · {b.category || "—"}</div>
              </div>
              <div className="text-xs text-[#8a6f2e]">{fmt(b.updated_at || b.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
